import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDataSubmit, updateQuizData } from 'utils/firebaseFunction';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import './EditQuiz.scss';

const EditQuiz = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [step, setStep] = useState('card'); // 'card' or 'questions'

  useEffect(() => {
    fetchQuizData();
  }, [quizId]);

  useEffect(() => {
    if (quiz?.questions && currentQuestionIndex >= quiz.questions.length) {
      setCurrentQuestionIndex(Math.max(0, quiz.questions.length - 1));
    }
  }, [quiz?.questions]);

  const fetchQuizData = async () => {
    try {
      const data = await getDataSubmit(quizId);
      console.log('Quiz Data:', data);
      if (data && data.quiz) {
        const quizData = {
          ...data.quiz,
          title: data.quiz.title || '',
          description: data.quiz.description || '',
          questions: data.quiz.questions?.map(q => ({
            question: q.question || '',
            text: q.text || q.question || '',
            options: q.options || [],
            correctAnswer: q.correctAnswer || 0
          })) || []
        };
        console.log('Formatted Quiz Data:', quizData);
        setQuiz(quizData);
      }
    } catch (error) {
      setError('حدث خطأ في جلب بيانات الاختبار');
      console.error('Error fetching quiz:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateQuiz = async (e) => {
    e.preventDefault();
    try {
      const updatedQuiz = {
        ...quiz,
        questions: quiz.questions.map(q => ({
          question: q.text || q.question,
          text: q.text || q.question,
          options: q.options,
          correctAnswer: q.correctAnswer
        }))
      };

      const result = await updateQuizData(quizId, updatedQuiz);
      if (result.success) {
        alert('تم تحديث الاختبار بنجاح');
        navigate('/admin');
      } else {
        setError(result.error || 'حدث خطأ في تحديث الاختبار');
      }
    } catch (error) {
      setError('حدث خطأ في تحديث الاختبار');
      console.error('Error updating quiz:', error);
    }
  };

  const handleQuestionChange = (index, field, value) => {
    const updatedQuestions = [...quiz.questions];
    if (field === 'text' || field === 'question') {
      updatedQuestions[index] = {
        ...updatedQuestions[index],
        text: value,
        question: value
      };
    } else {
      updatedQuestions[index] = {
        ...updatedQuestions[index],
        [field]: value
      };
    }
    setQuiz({ ...quiz, questions: updatedQuestions });
  };

  const handleAddQuestion = () => {
    const newQuestion = {
      question: '',
      text: '',
      options: ['', '', '', ''],
      correctAnswer: 0,
    };
    setQuiz({
      ...quiz,
      questions: [...(quiz.questions || []), newQuestion],
    });
    setCurrentQuestionIndex(quiz.questions.length);
  };

  const handleDeleteQuestion = (index) => {
    const updatedQuestions = quiz.questions.filter((_, i) => i !== index);
    if (index === currentQuestionIndex && index > 0) {
      setCurrentQuestionIndex(index - 1);
    }
    setQuiz({
      ...quiz,
      questions: updatedQuestions,
    });
  };

  const currentQuestion = quiz?.questions?.[currentQuestionIndex];

  if (loading) return <LoadingSpinner size="large" text="جاري تحميل بيانات الاختبار..." />;
  if (error) return <div className="error">{error}</div>;
  if (!quiz) return <div className="error">لم يتم العثور على الاختبار</div>;

  return (
    <div className="edit-quiz-container">
      <div className="quiz-header">
        <h1>تعديل الاختبار</h1>
        <button className="back-btn" onClick={() => navigate('/admin')}>
          العودة للوحة التحكم
        </button>
      </div>
      
      <div className="steps-navigation">
        <button 
          className={`step-btn ${step === 'card' ? 'active' : ''}`}
          onClick={() => setStep('card')}
          type='button'
        >
          معلومات الاختبار
        </button>
        <button 
          className={`step-btn ${step === 'questions' ? 'active' : ''}`}
          onClick={() => setStep('questions')}
          type='button'
        >
          الأسئلة
        </button>
      </div>

      <form onSubmit={handleUpdateQuiz}>
        {step === 'card' ? (
          <div className="quiz-info-section">
            <div className="form-group">
              <label>عنوان الاختبار:</label>
              <input
                type="text"
                value={quiz.title || ''}
                onChange={(e) => setQuiz({ ...quiz, title: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>وصف الاختبار:</label>
              <textarea
                value={quiz.description || ''}
                onChange={(e) => setQuiz({ ...quiz, description: e.target.value })}
                rows="4"
              />
            </div>

            <div className="form-actions">
              <button type="button" className="next-btn cancel-btn" onClick={() => setStep('questions')}>
                التالي: تعديل الأسئلة
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="questions-navigation">
              <div className="questions-tabs">
                {quiz.questions.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`question-tab ${currentQuestionIndex === index ? 'active' : ''}`}
                    onClick={() => setCurrentQuestionIndex(index)}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  type="button"
                  className="add-question-tab"
                  onClick={handleAddQuestion}
                >
                  +
                </button>
              </div>
            </div>

            <div className="question-editor">
              {quiz.questions.length > 0 && currentQuestion ? (
                <div className="question-card">
                  <div className="question-header">
                    <h3>السؤال {currentQuestionIndex + 1}</h3>
                    <button 
                      type="button" 
                      className="delete-question-btn"
                      onClick={() => handleDeleteQuestion(currentQuestionIndex)}
                    >
                      حذف السؤال
                    </button>
                  </div>
                  
                  <div className="form-group question-text">
                    <label>نص السؤال:</label>
                    <textarea
                      value={currentQuestion?.text || currentQuestion?.question || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleQuestionChange(currentQuestionIndex, 'text', value);
                      }}
                      required
                      placeholder="اكتب نص السؤال هنا"
                      rows="3"
                      className="question-textarea"
                    />
                  </div>

                  <div className="options-section">
                    <label>الخيارات:</label>
                    <div className="options-grid">
                      {currentQuestion.options.map((option, optIndex) => (
                        <div key={optIndex} className="option-item">
                          <div className="option-number">{optIndex + 1}</div>
                          <input
                            type="text"
                            value={option || ''}
                            onChange={(e) => {
                              const newOptions = [...currentQuestion.options];
                              newOptions[optIndex] = e.target.value;
                              handleQuestionChange(currentQuestionIndex, 'options', newOptions);
                            }}
                            required
                            placeholder={`الخيار ${optIndex + 1}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="correct-answer-section">
                    <label>الإجابة الصحيحة:</label>
                    <div className="options-buttons">
                      {currentQuestion.options.map((_, optIndex) => (
                        <button
                          key={optIndex}
                          type="button"
                          className={`option-btn ${currentQuestion.correctAnswer === optIndex ? 'correct' : ''}`}
                          onClick={() => handleQuestionChange(currentQuestionIndex, 'correctAnswer', optIndex)}
                        >
                          {optIndex + 1}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="no-questions">
                  <p>لا توجد أسئلة بعد</p>
                  <button type="button" className="add-first-question" onClick={handleAddQuestion}>
                    إضافة السؤال الأول
                  </button>
                </div>
              )}
            </div>

            <div className="form-actions">
            
              <button type="submit" className="save-btn">حفظ التغييرات</button>
              <button type="button" className="cancel-btn" onClick={() => navigate('/admin')}>
                إلغاء
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default EditQuiz; 